import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  AUFGABEN_GROESSEN,
  GROESSE_LABELS,
  TASK_STATUS_LABELS,
  TASK_STATUSES,
  ZEITFENSTER,
  ZEITFENSTER_LABELS,
  type Aufgabengroesse,
  type TaskStatus,
  type Zeitfenster,
  zaehltAlsEntwickler,
} from '@netcon-work/shared'
import { poolsQuery, triageVorschauQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { AufgabenTitel } from './AufgabeKontext'
import { Button } from './ui/primitives'
import { usePersonenDieserWelt } from '../lib/welt'

/**
 * **Eine Regel anlegen – mit Vorschau, bevor sie existiert** (Phase 6).
 *
 * ## Warum die Vorschau der eigentliche Inhalt ist
 *
 * Eine Regel zu formulieren ist leicht; zu wissen, was sie trifft, ist es nicht. Ohne
 * Vorschau müsste man sie anlegen, um es herauszufinden – und eine angelegte Regel, die zu
 * breit ist, hat schon einmal in einer Liste gestanden, in der jemand sie für richtig hielt.
 *
 * Gezeigt wird deshalb **während des Tippens**, was die Regel im ganzen Bestand erwischt,
 * getrennt nach **offen** und **erledigt**. Die Trennung ist die eigentliche Warnung: dass
 * eine Regel 16 erledigte PRTG-Alarme trifft, ist gut; dass sie zwei offene Aufgaben
 * mitnimmt, ist die Frage, die man beantworten muss.
 */
export function TriageRegelAnlegen({ fertig }: { fertig: () => void }) {
  const queryClient = useQueryClient()
  const { data: pools = [] } = useQuery(poolsQuery)

  const [name, setName] = useState('')
  const [feld, setFeld] = useState<'titel' | 'beschreibung' | 'bereich'>('titel')
  const [operator, setOperator] = useState<'beginnt_mit' | 'enthaelt' | 'ist_gleich'>('beginnt_mit')
  const [wert, setWert] = useState('')
  const [status, setStatus] = useState<TaskStatus | ''>('zurueckgestellt')
  /**
   * **Zeitfenster und Größe** (19.08.2026, [E-147](../../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Nils: *„ich brauche das in der Planung alles."* Die Zeitachse leitet ihr Datum unter
   * anderem aus dem Zeitfenster ab, und im ganzen Zulauf gibt es nichts, das eines erzeugt –
   * gemessen am 19.08.2026 trugen **7 von 122** offenen Aufgaben eines, alle von Hand. Eine Regel ist die billigste Quelle:
   * *„alles aus dieser Warteschlange ist `jetzt` und `klein`"* schreibt man einmal auf.
   */
  const [zeitfenster, setZeitfenster] = useState<Zeitfenster | ''>('')
  const [groesse, setGroesse] = useState<Aufgabengroesse | ''>('')
  const [pool, setPool] = useState('rauschen')
  const [fehler, setFehler] = useState<string | null>(null)
  /**
   * **Wer für so etwas in Frage kommt** (18.08.2026, [E-128](../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Nils: *„wenn was mit Portierung vorkommt, immer z"*. Das ist eine Regel über Personen,
   * und sie steht deshalb hier neben Status und Pool – nicht in einer eigenen Regelart.
   *
   * **Nur Entwickler:innen**, wie überall bei „kommen in Frage": eine Aufgabe wird von
   * jemandem bearbeitet, der bearbeitet.
   */
  /**
   * **Nur wer in dieser Arbeitswelt ist** (Z-07, 26.08.2026). Wer nicht Mitglied ist, sähe
   * die Aufgabe nie – eine Regel gilt je Arbeitswelt und ordnet in deren Pools ein.
   */
  const { personen } = usePersonenDieserWelt()
  const kandidaten = personen.filter((p) => p.isActive && zaehltAlsEntwickler(p))
  const [wunschIds, setWunschIds] = useState<string[]>([])

  const entwurf = { feld, operator, wert: wert.trim(), sourceId: null }
  const { data: vorschau, isFetching } = useQuery({
    ...triageVorschauQuery(entwurf),
    enabled: wert.trim().length >= 2,
  })

  const anlegen = useMutation({
    mutationFn: (v: Parameters<typeof trpc.triage.anlegen.mutate>[0]) =>
      trpc.triage.anlegen.mutate(v),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['triage'] })
      fertig()
    },
    onError: (f: Error) => setFehler(f.message),
  })

  const offene = vorschau?.treffer.filter((t) => t.offen) ?? []
  const erledigte = vorschau?.treffer.filter((t) => !t.offen) ?? []

  return (
    <div className="mt-3 rounded-lg border border-line bg-[#fafaf8] p-3">
      <h4 className="mb-2 text-[12.5px] font-bold">Neue Regel</h4>

      <div className="flex flex-wrap items-end gap-2 text-[12px]">
        <label>
          <span className="block text-muted">Name</span>
          <input
            className="mt-0.5 w-[220px] rounded-md border border-line px-2 py-1"
            placeholder="z. B. Wünsche in den Projekte-Pool"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label>
          <span className="block text-muted">prüft</span>
          <select
            className="mt-0.5 rounded-md border border-line px-2 py-1"
            value={feld}
            onChange={(e) => setFeld(e.target.value as 'titel')}
          >
            <option value="titel">Titel</option>
            <option value="beschreibung">Beschreibung</option>
            <option value="bereich">Bereich</option>
          </select>
        </label>

        <label>
          <span className="block text-muted">&nbsp;</span>
          <select
            className="mt-0.5 rounded-md border border-line px-2 py-1"
            value={operator}
            onChange={(e) => setOperator(e.target.value as 'enthaelt')}
          >
            <option value="beginnt_mit">beginnt mit</option>
            <option value="enthaelt">enthält</option>
            <option value="ist_gleich">ist gleich</option>
          </select>
        </label>

        <label>
          <span className="block text-muted">Wert</span>
          <input
            className="mt-0.5 w-[200px] rounded-md border border-line px-2 py-1 font-mono text-[11.5px]"
            placeholder="Wünsche"
            value={wert}
            onChange={(e) => setWert(e.target.value)}
          />
        </label>

        <label>
          <span className="block text-muted">setzt Status</span>
          <select
            className="mt-0.5 rounded-md border border-line px-2 py-1"
            value={status}
            onChange={(e) => setStatus(e.target.value as TaskStatus | '')}
          >
            <option value="">– keinen –</option>
            {TASK_STATUSES.map((s) => (
              <option key={s} value={s}>
                {TASK_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="block text-muted">und Pool</span>
          <select
            className="mt-0.5 rounded-md border border-line px-2 py-1"
            value={pool}
            onChange={(e) => setPool(e.target.value)}
          >
            <option value="">– keinen –</option>
            {pools.map((p) => (
              <option key={p.id} value={p.id}>
                {p.icon} {p.label}
              </option>
            ))}
          </select>
        </label>

        {/**
         * **Zeitfenster und Größe stehen neben Status und Pool, nicht darunter.** Anders als
         * die Personenvorschläge sind sie Angaben *an der Aufgabe*, von denen es je Aufgabe
         * genau eine gibt – dieselbe Sorte Aussage wie Status und Pool.
         */}
        <label>
          <span className="block text-muted">und Zeitfenster</span>
          <select
            className="mt-0.5 rounded-md border border-line px-2 py-1"
            value={zeitfenster}
            onChange={(e) => setZeitfenster(e.target.value as Zeitfenster | '')}
          >
            <option value="">– keins –</option>
            {ZEITFENSTER.map((z) => (
              <option key={z} value={z}>
                {ZEITFENSTER_LABELS[z]}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="block text-muted">und Größe</span>
          <select
            className="mt-0.5 rounded-md border border-line px-2 py-1"
            value={groesse}
            onChange={(e) => setGroesse(e.target.value as Aufgabengroesse | '')}
          >
            <option value="">– keine –</option>
            {AUFGABEN_GROESSEN.map((g) => (
              <option key={g} value={g}>
                {GROESSE_LABELS[g]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/**
       * **Personen als eigene Zeile.** Sie sind die andere Sorte Aussage: Status und Pool
       * *ordnen ein* (davon gibt es je Aufgabe eine), Namen *schlagen vor* (davon gibt es
       * mehrere, auch aus mehreren Regeln). Nebeneinander in derselben Zeile sähe es aus,
       * als schlössen sie einander aus.
       */}
      {kandidaten.length > 0 && (
        <div className="mt-2 border-t border-line pt-2 text-[12px]">
          <span className="text-muted">und schlägt vor, wer in Frage kommt</span>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {kandidaten.map((p) => {
              const an = wunschIds.includes(p.id)
              return (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={an}
                  onClick={() =>
                    setWunschIds((alt) =>
                      alt.includes(p.id) ? alt.filter((x) => x !== p.id) : [...alt, p.id],
                    )
                  }
                  className={`cursor-pointer rounded-lg px-[10px] py-[3px] text-[11.5px] font-semibold ${
                    an ? 'bg-ink text-white' : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]'
                  }`}
                >
                  {p.displayName}
                </button>
              )
            })}
          </div>
          {wunschIds.length > 0 && (
            <p className="mt-1.5 text-[11px] text-muted">
              Wird <strong>nicht</strong> eingetragen – der Vorschlag steht im Eingang an der
              Aufgabe, mit einem Knopf zum Übernehmen. Mehrere greifende Regeln legen ihre Namen
              zusammen.
            </p>
          )}
          {/**
           * **Die Voreinstellung ist für Rauschen gemacht** – „zurückstellen, Pool Rauschen".
           * Wer eine Personenregel schreibt, will das fast nie: die Aufgabe soll bearbeitet
           * werden, nicht verschwinden. Der Hinweis kommt hier und nicht als Sperre, weil es
           * Fälle gibt, in denen beides zusammen richtig ist.
           */}
          {wunschIds.length > 0 && (status !== '' || pool !== '') && (
            <p className="mt-1 rounded-md bg-[#fdf3dc] px-2 py-1 text-[11px] text-[#8a6100]">
              ⚠ Diese Regel ordnet <strong>zusätzlich</strong> ein
              {status ? ` (Status „${TASK_STATUS_LABELS[status]}")` : ''}
              {pool ? ` (Pool „${pools.find((p) => p.id === pool)?.label ?? pool}")` : ''}. Für eine
              reine „wer kommt in Frage"-Regel beide auf „keinen" stellen.
            </p>
          )}
        </div>
      )}

      {/* ── Vorschau ────────────────────────────────────────── */}
      {wert.trim().length >= 2 && (
        <div className="mt-3 border-t border-line pt-2">
          {isFetching && !vorschau ? (
            <p className="text-[12px] text-muted">wird geprüft …</p>
          ) : !vorschau ? null : vorschau.treffer.length === 0 ? (
            <p className="text-[12px] text-muted">
              Trifft <strong>keine</strong> der {vorschau.geprueft} Aufgaben. Eine Regel, die nichts
              trifft, ist entweder überflüssig oder falsch geschrieben.
            </p>
          ) : (
            <>
              <p className="text-[12px]">
                Trifft <strong>{vorschau.treffer.length}</strong> von {vorschau.geprueft} Aufgaben:{' '}
                {/**
                 * **Die Trennung ist die Warnung.** Dass eine Regel erledigte Alarme trifft,
                 * ist gut; dass sie offene Aufgaben mitnimmt, ist die Frage, die vor dem
                 * Speichern zu beantworten ist.
                 */}
                <strong className={offene.length > 0 ? 'text-pool-sofort' : ''}>
                  {offene.length} offen
                </strong>
                , {erledigte.length} erledigt.
              </p>
              {offene.length > 0 && (
                <ul className="mt-1.5 grid gap-0.5 text-[12px] text-ink2">
                  {offene.slice(0, 12).map((t) => (
                    <li key={t.taskId}>
                      <AufgabenTitel aufgabeId={t.taskId} titel={t.titel} />
                    </li>
                  ))}
                  {offene.length > 12 && (
                    <li className="text-muted">… und {offene.length - 12} weitere offene</li>
                  )}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {fehler && <p className="mt-2 text-[12px] text-pool-sofort">{fehler}</p>}

      <div className="mt-3 flex items-center gap-2">
        <Button
          disabled={name.trim().length < 3 || wert.trim().length < 2 || anlegen.isPending}
          onClick={() => {
            setFehler(null)
            anlegen.mutate({
              name: name.trim(),
              feld,
              operator,
              wert: wert.trim(),
              sourceId: null,
              /**
               * **Keine Reihenfolge mehr von hier** (20.08.2026,
               * [E-153](../../../../docs/ENTSCHEIDUNGEN.md)). Hier stand fest `100` – bei
               * jeder Regel, ohne dass die Maske das Feld je gezeigt hätte. Fünf Regeln mit
               * demselben Rang später entschied „die erste greifende Regel gewinnt" in
               * Wahrheit die Datenbank. Der Server hängt neue Regeln jetzt hinten an;
               * gerückt wird mit den Pfeilen in der Liste.
               */
              ...(status ? { status } : {}),
              ...(zeitfenster ? { zeitfenster } : {}),
              ...(groesse ? { groesse } : {}),
              poolIds: pool ? [pool] : [],
              wunschPersonIds: wunschIds,
            })
          }}
        >
          Anlegen
        </Button>
        <Button variant="ghost" onClick={fertig}>
          Abbrechen
        </Button>
        {/**
         * **Der Hinweis gehört neben den Knopf.** Wer eine Regel anlegt, hat meist eine
         * Absicht – und würde sonst erwarten, dass sie sofort wirkt.
         */}
        <span className="text-[11px] text-muted">
          Neue Regeln beginnen bei „nur vorschlagen" und wirken erst nach dem Hochstufen.
        </span>
      </div>
    </div>
  )
}
